import { useEffect, useRef, useState } from "react";

/** From this many minutes of a barrier read up, the status block warns. */
export const BARRIER_UP_WARNING_MINUTES = 5;

/** How often a mounted block re-checks the clock while the barrier reads up. */
const BARRIER_UP_TICK_MS = 15_000;

export interface BarrierUpTracker {
  /** Records one reading. The first "up" reading for a gate starts the clock; a "down" or unknown reading, or another gate, resets it. */
  observe: (gateKey: string, barrierUp: boolean | undefined) => void;
  /** Whole minutes since the first "up" reading for this gate, from the warning threshold on; otherwise undefined. */
  minutesUp: (gateKey: string) => number | undefined;
}

/**
 * Remembers when the barrier was first read up. It only counts from when this screen first saw it, so the
 * minutes are a lower bound ("at least"), never the real time the barrier has been up.
 */
export function createBarrierUpTracker(): BarrierUpTracker {
  let since: { gateKey: string; at: number } | undefined;

  return {
    observe(gateKey, barrierUp) {
      if (!barrierUp) {
        since = undefined;
      } else if (since?.gateKey !== gateKey) {
        since = { gateKey, at: Date.now() };
      }
    },
    minutesUp(gateKey) {
      if (since?.gateKey !== gateKey) return undefined;
      const minutes = Math.floor((Date.now() - since.at) / 60_000);
      return minutes >= BARRIER_UP_WARNING_MINUTES ? minutes : undefined;
    },
  };
}

/**
 * Minutes the barrier has read up for this gate since this screen first saw it, once that reaches
 * {@link BARRIER_UP_WARNING_MINUTES}; undefined before that, when it reads down or unknown, or after the gate changes.
 * Pass `undefined` for `barrierUp` while there is no reading.
 */
export function useBarrierUpMinutes(gateKey: string, barrierUp: boolean | undefined): number | undefined {
  const trackerRef = useRef<BarrierUpTracker | null>(null);
  if (!trackerRef.current) trackerRef.current = createBarrierUpTracker();
  const tracker = trackerRef.current;

  const [, setTick] = useState(0);

  useEffect(() => {
    tracker.observe(gateKey, barrierUp);
    if (!barrierUp) return undefined;
    const id = setInterval(() => setTick((tick) => tick + 1), BARRIER_UP_TICK_MS);
    return () => clearInterval(id);
  }, [tracker, gateKey, barrierUp]);

  // The effect has not run yet on the render that changes the reading, so a stale clock must not leak into it.
  return barrierUp ? tracker.minutesUp(gateKey) : undefined;
}
